import React, { useEffect, useState, useMemo } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box,
    Button, TextField, MenuItem, FormControl, InputLabel, Select, RadioGroup, FormControlLabel, Radio, Grid
} from '@mui/material';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from 'dayjs';
import { useReferenceData } from '../ReferenceDataContext';
import { useApiClient } from '../useApiClient';
import useAuthToken from '../../functions/useAuthToken';
import { Valuation, ValuationType, ValuerOption } from '../../models/Valuation';
import { Organisation } from '../../models/Organisation';

//Error Handling
import { useErrorHandler, isNullOrEmpty } from '../../hooks/useErrorHandler';
import ErrorSummary from '../ErrorHandler/ErrorSummary';

type Props = {
    open: boolean;
    onClose: () => void;
    artworkId: number;
    valuation: Valuation | null;
    onSave: (valuationData: FormData) => void;
};
const AddValuationDialog: React.FC<Props> = (
    { open, onClose, artworkId, valuation, onSave }) => {

    const isEditMode = valuation !== null;
    const { errors, handleError, clearErrors, validateRequired, hasErrors } = useErrorHandler();

    // valuer selection
    type ValuerSource = 'client' | 'organisation' | 'external';
    const isValidValuerSource = (source: string): source is ValuerSource => {
        return ['client', 'organisation', 'external'].includes(source);
    };
    const toValuerSource = (source: string): ValuerSource => {
        if (isValidValuerSource(source)) {
            return source;
        }
        // Return a default or throw error
        return 'client';
    };

    const [valuerSource, setValuerSource] = useState<ValuerSource>('client');

    const [valuationTypes, setValuations] = useState<ValuationType[]>([]);

    const getAccessToken = useAuthToken();
    const { get, put, putJson, post, postJson, delete: del } = useApiClient();

    const { currencies, isReady, loadingAny, errorAny, refresh } = useReferenceData();
    const [valuationDate, setValuationDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [valuationTypeId, setValuationTypeId] = useState<number>(valuationTypes[0]?.id ?? 1);
    const [originalValuationTypeId, setOriginalValuationTypeId] = useState<number>(0);

    const [amount, setAmount] = useState<string>('');                 
    const [estimateLow, setEstimateLow] = useState<string>('');       
    const [estimateHigh, setEstimateHigh] = useState<string>('');

    const [currencyCode, setCurrencyCode] = useState<string>('USD');

    const [valuerUserId, setValuerUserId] = useState<number | ''>('');
    const [valuerClientUserId, setClientValuerUserId] = useState<number | ''>('');
    const [valuerOrganisationId, setValuerOrganisationId] = useState<number | ''>('');
    const [valuerOrganisationUserId, setValuerOrganisationUserId] = useState<number | ''>('');
    const [organisationValuers, setOrganisationValuers] = useState<ValuerOption[]>([]);
    const [externalValuerName, setExternalValuerName] = useState<string>('');

    const [clientValuers, setClientValuers] = useState<ValuerOption[]>([]);
    const [organisations, setOrganisations] = useState<Organisation[]>([]);

    const selectedType = useMemo(
        () => valuationTypes.find(v => v.id === valuationTypeId),
        [valuationTypeId, valuationTypes]
    );
    const isAuction = selectedType?.type === 'Auction Estimate';

    const formatDateForInput = (date: Date | string): string => {
        return new Date(date).toISOString().split('T')[0];
    };

    useEffect(() => {

        if (isEditMode) {
            setValuationTypeId(valuation.valuationTypeId);
            setOriginalValuationTypeId(valuation.valuationTypeId);
            setValuationDate(valuation.valuationDate ?? '');
            setAmount(valuation.lowerRangeAmount.toString());
            setEstimateLow(valuation.lowerRangeAmount.toString());
            setEstimateHigh(valuation.upperRangeAmount.toString());
            setCurrencyCode(valuation.currencyCode);
            setValuerSource(toValuerSource(valuation.valuerType ?? ''));
            setClientValuerUserId(valuation.valuerUserId);
            setValuerUserId(valuation.valuerUserId);
            setValuerOrganisationId(valuation.valuerOrganisationId ?? "");
            setExternalValuerName(valuation.externalValuer ?? "");
        } else {
            setValuationTypeId(2);
            setValuationDate('');
            setAmount("");
            setEstimateLow("");
            setEstimateHigh("");
            setCurrencyCode("USD");
            setValuerSource(toValuerSource(''));
            setClientValuerUserId("");
            setValuerUserId("");
            setValuerOrganisationId("");
            setExternalValuerName("");
        }
    }, [valuation, isEditMode]);

    const addValuation = async () => {

        try {
            const currLow = Number(estimateLow);
            const currHigh = Number(estimateHigh);
            let currLowVal = isNaN(currLow) ? 0 : currLow;
            let currHighVal = isNaN(currHigh) ? 0 : currHigh;

            if (currLowVal > currHighVal && currHighVal > 0) {
                currLowVal = currHighVal;
                currHighVal = Number(estimateLow);
            }

            if (currHighVal < currLowVal) {
                currHighVal = currLowVal
            }

            const dateOfValuation = dayjs(valuationDate).format('YYYY-MM-DD');

            const formData = new FormData();
            formData.append("ArtworkId", artworkId.toString());
            formData.append("ValuationTypeId", valuationTypeId.toString());
            formData.append("ValuationDate", dateOfValuation);
            formData.append("LowerRangeAmount", currLowVal.toString());
            formData.append("UpperRangeAmount", currHighVal.toString());
            formData.append("CurrencyCode", currencyCode);
            formData.append("ValuerType", valuerSource.toString());
            formData.append("ValuerUserId", valuerUserId?.toString() || "");
            formData.append("ValuerOrganisationId", valuerOrganisationId?.toString() || "");
            formData.append("ExternalValuer", externalValuerName?.toString() || "");

                onSave(formData);
        }
        catch (ex) {
            console.warn(ex);
            return false;
        }
    }

    const handleSave = async () => {

        //Validate Form
        clearErrors();
        let isValid = true;

        const validateValuationTypeId = valuationTypeId === 0 ? null : valuationTypeId?.toString() ?? null;

        if (!validateRequired(valuationDate, 'Date', 'Please set a valuation date.')) isValid = false;
        if (!validateRequired(validateValuationTypeId, 'Valuation Type', 'Please select a valuation type.')) isValid = false;

        if (valuationTypeId === 1 && originalValuationTypeId != 1 || valuationTypeId === 1 && !isEditMode ) {
            handleError({ code: 'Valuation Type', message: 'You cannot manually create Acquisition valuation type.', details: 'Please select a different valuation type.' });
            isValid = false;
        }

        if (isValid) {
            addValuation();
            onClose();
        }
    };

    const onValuationChange = async (newValuation: string) => {
        setAmount(newValuation);
        setEstimateLow(newValuation);
        setEstimateHigh(newValuation);
    }

    const onLowEstimageChange = async (newValuation: string) => {
        const newLow = Number(newValuation);
        const currHigh = Number(estimateHigh);
        const currHighVal = isNaN(currHigh) ? 0 : currHigh;

        setEstimateLow(newValuation);

        //if (currHighVal < newLow) {
        //    setEstimateHigh(newValuation);
        //}
    }

    const onHighEstimageChange = async (newValuation: string) => {
        const newHigh = Number(newValuation);
        const currLow = Number(estimateLow);
        const currLowVal = isNaN(currLow) ? 0 : currLow;

        setEstimateHigh(newValuation);

        //if (currLowVal > newHigh) {
        //    setEstimateLow(newValuation);
        //}
    }

    const onChangeClientValuer = async (valuerId: string) => {
        const id = Number(valuerId);
        setClientValuerUserId(id);
        setValuerUserId(id);
    }

    const onChangeOrgValuer = async (valuerId: string) => {
        const id = Number(valuerId);
        setValuerOrganisationUserId(id);
        setValuerUserId(id);
    }

    const onChangeOrganisation = async (orgIdStr: string) => {
        const id = Number(orgIdStr);
        setValuerOrganisationId(id || '');
        setValuerOrganisationUserId('');
        if (id) {
            const accessToken = await getAccessToken();
            if (!accessToken) {
                setOrganisationValuers([]);
            }
            else {

                const list = await get<ValuerOption[]>(`organisations/valuers/${id}`, accessToken);

                setOrganisationValuers(list);
            }
        } else {
            setOrganisationValuers([]);
        }
    };

    useEffect(() => {

        const getValuationTypes = async () => {
            const accessToken = await getAccessToken();
            if (!accessToken) return;

            try {

                //Valuation Types
                const valTypes = await get<ValuationType[]>('ValuationTypes', accessToken);
                //const availableTypes = valTypes.filter(item => item.id !== 1); // Id 1 = Acquisition - this is only set once on Item add.
                setValuations(valTypes);

                //Valuers
                const valuerRes = await get<ValuerOption[]>('user/valuers', accessToken);
                setClientValuers(valuerRes);

                //Organisations
                const orgRes = await get<Organisation[]>('organisations/auction-houses', accessToken);
                setOrganisations(orgRes);

            } catch (error) {
                console.error("Error fetching artwork/valuations:", error);
            } finally {
                //setDataLoading(false);
            }
        };

        getValuationTypes();

    }, []);

    if (!isReady && loadingAny) return <p>Loading reference data…</p>;

    if (!isReady && errorAny) {
        return (
            <div className="bg-yellow-50 border border-yellow-300 p-3 rounded">
                <p>{errorAny}</p>
                <button className="mt-2 px-3 py-1 rounded border" onClick={() => refresh()}>
                    Retry loading references
                </button>
            </div>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{isEditMode ? 'Edit Value' : 'Add New Value'}</DialogTitle>
            <hr></hr>
            <DialogContent>
                {/* Error Panel*/}
                <Box>
                    <ErrorSummary errors={errors} title="Artwork Errors" />
                </Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <DatePicker
                            label="Valuation Date"
                            value={valuationDate ? dayjs(valuationDate) : null}
                            onChange={(e) => setValuationDate(e?.toISOString() ?? '')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="vt-label">Valuation Type</InputLabel>
                            <Select
                                labelId="vt-label"
                                label="Valuation Type"
                                value={valuationTypeId}
                                onChange={(e) => setValuationTypeId(Number(e.target.value))}
                            >
                                {valuationTypes.map(v => (
                                    <MenuItem key={v.id} value={v.id}>{v.type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Amount vs Range */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="cur-label">Currency</InputLabel>
                                <Select
                                    labelId="cur-label"
                                    label="Currency"
                                    value={currencyCode}
                                    onChange={e => setCurrencyCode(String(e.target.value))}
                                >
                                {currencies?.map(c => <MenuItem key={c.currencyCode} value={c.currencyCode}>{c.currencyCode} — {c.currencyName}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        {!isAuction ? (
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Value"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={amount}
                                onChange={e => onValuationChange(e.target.value)}
                                />
                            </Grid>
                        ) : (
                            <>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        label="Low Estimate"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={estimateLow}
                                        onChange={e => onLowEstimageChange(e.target.value)}
                                    />
                                </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                        label="High Estimate"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={estimateHigh}
                                        onChange={e => onHighEstimageChange(e.target.value)}
                                    />
                                </Grid>
                                    </>
                        
                        )}
                    {/* Valuer source */}
                    <Grid size={{ xs: 12 }}>
                        <RadioGroup
                            row
                            value={valuerSource}
                            onChange={(_, v) => setValuerSource(v as any)}
                        >
                            <FormControlLabel value="client" control={<Radio />} label="Client Valuer" />
                            <FormControlLabel value="organisation" control={<Radio />} label="Auction House" />
                            <FormControlLabel value="external" control={<Radio />} label="Other" />
                        </RadioGroup>
                    </Grid>

                    {valuerSource === 'client' && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="client-valuer-label">Client Valuer</InputLabel>
                                <Select
                                    labelId="client-valuer-label"
                                    label="Client Valuer"
                                    value={valuerClientUserId}
                                    onChange={e => onChangeClientValuer(String(e.target.value))}
                                >
                                    {clientValuers.map(v => <MenuItem key={v.userId} value={v.userId}>{v.displayName}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {valuerSource === 'organisation' && (
                        <>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="org-label">Auction House</InputLabel>
                                    <Select
                                        labelId="org-label"
                                        label="Organisation"
                                        value={valuerOrganisationId}
                                        onChange={(e) => onChangeOrganisation(String(e.target.value))}
                                    >
                                        <MenuItem value="">— Select —</MenuItem>
                                        {organisations.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {!!valuerOrganisationId && (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="org-user-label">Valuer</InputLabel>
                                        <Select
                                            labelId="org-user-label"
                                            label="Organisation Valuer"
                                            value={valuerOrganisationUserId}
                                            onChange={e => onChangeOrgValuer(String(e.target.value))}
                                        >
                                            <MenuItem value="">— None —</MenuItem>
                                            {organisationValuers.map(u => <MenuItem key={u.userId} value={u.userId}>{u.displayName}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                        </>
                    )}

                    {valuerSource === 'external' && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label="Valuer name/organisation"
                                fullWidth size="small"
                                value={externalValuerName}
                                onChange={e => setExternalValuerName(e.target.value)}
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddValuationDialog;
